import { ref, onUnmounted } from 'vue'
import { db } from '@/firebase'
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc, 
  Timestamp 
} from 'firebase/firestore'
import { 
  shouldTransitionToInProgress, 
  shouldTransitionToCompleted, 
  getAutoSessionStatus 
} from '@/utils/sessionTimeUtils'
import { sessionService } from '@/services/sessionService'

interface SessionForMonitoring {
  id: string
  sessionStatus: string
  startAt: Timestamp
  endAt: Timestamp
}

export class SessionStatusMonitor {
  private unsubscribers: (() => void)[] = []
  private intervals: number[] = []
  private isActive = false

  /**
   * Start monitoring sessions for automatic status updates
   */
  startMonitoring() {
    if (this.isActive) return
    this.isActive = true

    // Monitor confirmed sessions that might need to transition to in-progress
    this.monitorSessionTransitions('confirmed')
    
    // Monitor in-progress sessions that might need to transition to completed
    this.monitorSessionTransitions('in-progress')

    // Set up periodic check every minute for any missed transitions
    const periodicCheck = window.setInterval(() => {
      this.performPeriodicStatusCheck()
    }, 60000) // Check every minute

    this.intervals.push(periodicCheck)
  }

  /**
   * Stop monitoring sessions
   */
  stopMonitoring() {
    this.isActive = false
    
    // Unsubscribe from all Firestore listeners
    this.unsubscribers.forEach(unsubscribe => unsubscribe())
    this.unsubscribers = []
    
    // Clear all intervals
    this.intervals.forEach(interval => window.clearInterval(interval))
    this.intervals = []
  }

  /**
   * Monitor sessions with specific status for transitions
   */
  private monitorSessionTransitions(status: 'confirmed' | 'in-progress') {
    const sessionsRef = collection(db, 'sessions')
    const sessionsQuery = query(
      sessionsRef,
      where('sessionStatus', '==', status),
      where('status', '==', 'active') // Only active sessions
    )

    const unsubscribe = onSnapshot(sessionsQuery, (snapshot) => {
      snapshot.docs.forEach(async (docSnapshot) => {
        const sessionData = docSnapshot.data() as SessionForMonitoring
        const sessionId = docSnapshot.id

        await this.checkAndUpdateSessionStatus(sessionId, sessionData)
      })
    })

    this.unsubscribers.push(unsubscribe)
  }

  /**
   * Check if a session needs status update and perform it
   */
  private async checkAndUpdateSessionStatus(sessionId: string, sessionData: SessionForMonitoring) {
    try {
      const startTime = sessionData.startAt.toDate()
      const endTime = sessionData.endAt.toDate()
      const currentStatus = sessionData.sessionStatus

      const newStatus = getAutoSessionStatus(startTime, endTime, currentStatus)

      // Only update if status has changed
      if (newStatus !== currentStatus) {
        const reason = this.getTransitionReason(currentStatus, newStatus)
        await sessionService.updateSessionStatusAuto(sessionId, newStatus as any, reason)
      }
    } catch (error) {
      // Error checking session status - fail silently
    }
  }

  /**
   * Get the appropriate session status based on current time
   */
  private getTransitionReason(fromStatus: string, toStatus: string): string {
    if (fromStatus === 'confirmed' && toStatus === 'in-progress') {
      return 'Session start time reached'
    }
    if (fromStatus === 'in-progress' && toStatus === 'completed') {
      return 'Session end time reached'
    }
    return `Status changed from ${fromStatus} to ${toStatus}`
  }

  /**
   * Update session status in Firestore
   */
  private async updateSessionStatus(sessionId: string, newStatus: string) {
    try {
      const sessionRef = doc(db, 'sessions', sessionId)
      await updateDoc(sessionRef, {
        sessionStatus: newStatus,
        updatedAt: Timestamp.now()
      })
    } catch (error) {
      throw error
    }
  }

  /**
   * Perform a comprehensive check of all active sessions
   * This is a fallback to catch any sessions that might have been missed
   */
  private async performPeriodicStatusCheck() {
    if (!this.isActive) return

    try {
      // We could query all active sessions here, but for performance,
      // we'll rely on the real-time listeners for most updates
      // This is just a safety net that could be expanded if needed
    } catch (error) {
      // Error in periodic status check - fail silently
    }
  }
}

// Create a singleton instance
const sessionStatusMonitor = new SessionStatusMonitor()

/**
 * Composable for using session status monitoring
 */
export function useSessionStatusMonitor() {
  const isMonitoring = ref(false)

  const startMonitoring = () => {
    sessionStatusMonitor.startMonitoring()
    isMonitoring.value = true
  }

  const stopMonitoring = () => {
    sessionStatusMonitor.stopMonitoring()
    isMonitoring.value = false
  }

  // Cleanup on component unmount
  onUnmounted(() => {
    if (isMonitoring.value) {
      stopMonitoring()
    }
  })

  return {
    isMonitoring,
    startMonitoring,
    stopMonitoring
  }
}

// Export the singleton for direct use if needed
export { sessionStatusMonitor }